/**
 * Unified Blockscout API utility
 * 
 * Provides authenticated access to Blockscout REST API v2 for:
 * - Token transfers and trade history
 * - Pool liquidity data
 * - Deployment information
 * - Contract interactions
 * - Lock and vesting event tracking
 */

import { config } from './config';

// ==================== CONFIGURATION ====================

const BLOCKSCOUT_API_BASE = `${config.ink.explorerUrl}/api/v2`;
const BLOCKSCOUT_API_V1 = `${config.ink.explorerUrl}/api`;

// WETH address on Ink chain
const WETH_ADDRESS = '0x4200000000000000000000000000000000000006';

// ==================== API KEY ROTATION ====================
// Blockscout allows 10 RPS per key, 3 keys max = 30 RPS total
// We rotate through keys to distribute load

let currentKeyIndex = 0;

/**
 * Get next API key using round-robin rotation
 * This distributes requests across all available keys for max throughput
 */
function getNextApiKey(): string {
  const keys = config.ink.blockscoutApiKeys;
  if (!keys || keys.length === 0) return '';
  
  const key = keys[currentKeyIndex];
  currentKeyIndex = (currentKeyIndex + 1) % keys.length;
  return key;
}

// ==================== TYPES ====================

export interface BlockscoutTokenTransfer {
  block_hash: string;
  block_number: number;
  from: {
    hash: string;
    name?: string;
    is_contract?: boolean;
  };
  to: {
    hash: string;
    name?: string;
    is_contract?: boolean;
  };
  log_index: number;
  method?: string;
  timestamp: string;
  token: {
    address?: string;
    address_hash?: string; // Blockscout API v2 uses this
    decimals: string;
    name: string;
    symbol: string;
    type: string;
    icon_url?: string;
  };
  total: {
    decimals: string;
    value: string;
  };
  tx_hash?: string;
  transaction_hash?: string; // Some API versions use this instead
  type: string;
}

// Helper to get tx hash from transfer (handles both field names)
function getTxHash(transfer: BlockscoutTokenTransfer): string {
  return transfer.tx_hash || transfer.transaction_hash || '';
}

// Helper to get token address from transfer (handles both field names)
function getTokenAddress(transfer: BlockscoutTokenTransfer): string {
  return transfer.token?.address || transfer.token?.address_hash || '';
}

export interface BlockscoutTransaction {
  block: number;
  confirmations: number;
  fee: {
    type: string;
    value: string;
  };
  from: {
    hash: string;
    name?: string;
    is_contract?: boolean;
  };
  gas_limit: string;
  gas_price: string;
  gas_used: string;
  hash: string;
  method?: string;
  nonce: number;
  result: string;
  status: string;
  timestamp: string;
  to: {
    hash: string;
    name?: string;
    is_contract?: boolean;
  };
  type: number;
  value: string;
  decoded_input?: {
    method_call: string;
    method_id: string;
    parameters: any[];
  };
}

export interface BlockscoutAddressInfo {
  hash: string;
  creator_address_hash?: string;
  creation_tx_hash?: string;
  block_number_balance_updated_at?: number;
  coin_balance?: string;
  exchange_rate?: string;
  implementation_address?: string;
  implementation_name?: string;
  is_contract?: boolean;
  is_verified?: boolean;
  name?: string;
  token?: {
    address: string;
    decimals: string;
    name: string;
    symbol: string;
    type: string;
  };
}

export interface BlockscoutTokenBalance {
  token: {
    address: string;
    address_hash?: string; // Some APIs return address_hash instead of address
    decimals: string;
    name: string;
    symbol: string;
    type: string;
    icon_url?: string;
    exchange_rate?: string;
  };
  token_id?: string;
  token_instance?: any;
  value: string;
}

export interface BlockscoutLog {
  address: {
    hash: string;
    name?: string;
  };
  block_hash: string;
  block_number: number;
  data: string;
  decoded?: {
    method_call: string;
    method_id: string;
    parameters: any[];
  };
  index: number;
  smart_contract?: {
    address: {
      hash: string;
    };
    name?: string;
  };
  topics: string[];
  tx_hash: string;
}

export interface OrderbookTrade {
  type: 'buy' | 'sell';
  tokenAmount: number;
  ethAmount: number;
  pricePerToken: number;
  priceUSD: number;
  amountUSD: number;
  timestamp: number;
  txHash: string;
  maker: string;
  blockNumber: number;
}

export interface DeploymentInfo {
  contractAddress: string;
  creatorAddress: string;
  creationTxHash: string;
  blockNumber: number;
  timestamp: string;
  isContract: boolean;
  isVerified: boolean;
  name?: string;
}

export interface PoolLiquidity {
  poolAddress: string;
  token0: {
    address: string;
    symbol: string;
    name: string;
    decimals: number;
    balance: string;
    balanceFormatted: number;
  };
  token1: {
    address: string;
    symbol: string;
    name: string;
    decimals: number;
    balance: string;
    balanceFormatted: number;
  };
  totalValueUSD?: number;
}

export interface PaginatedResponse<T> {
  items: T[];
  next_page_params?: {
    block_number?: number;
    index?: number;
    items_count?: number;
  } | null;
}

// ==================== HELPER FUNCTIONS ====================

/**
 * Get headers with API key authentication using round-robin rotation
 * Rotates through available keys to maximize throughput (30 RPS with 3 keys)
 */
export const getBlockscoutHeaders = (): HeadersInit => {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  };
  
  const apiKey = getNextApiKey();
  if (apiKey) {
    // Blockscout uses Bearer token authentication
    headers['Authorization'] = `Bearer ${apiKey}`;
  }
  
  return headers;
};

/**
 * Get the number of API keys configured (useful for rate limit calculations)
 */
export const getApiKeyCount = (): number => {
  return config.ink.blockscoutApiKeys?.length || 0;
};

/**
 * Get the effective rate limit based on configured keys
 * Each key provides 10 RPS
 */
export const getEffectiveRateLimit = (): number => {
  const keyCount = getApiKeyCount();
  return keyCount > 0 ? keyCount * 10 : 5; // 5 RPS default without keys
};

/**
 * Make authenticated request to Blockscout API
 */
async function blockscoutFetch<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = endpoint.startsWith('http') ? endpoint : `${BLOCKSCOUT_API_BASE}${endpoint}`;
  
  const response = await fetch(url, {
    ...options,
    headers: {
      ...getBlockscoutHeaders(),
      ...options.headers,
    },
    signal: AbortSignal.timeout(15000), // 15 second timeout
  });

  if (!response.ok) {
    throw new Error(`Blockscout API error: ${response.status} ${response.statusText}`);
  }

  return response.json();
}

/**
 * Format token amount from raw value and decimals
 */
function formatTokenAmount(value: string, decimals: string | number): number {
  const dec = typeof decimals === 'string' ? parseInt(decimals) : decimals;
  return parseFloat(value) / Math.pow(10, dec);
}

// ==================== ADDRESS API ====================

/**
 * Get detailed information about an address (contract or EOA)
 */
export async function getAddressInfo(address: string): Promise<BlockscoutAddressInfo> {
  return blockscoutFetch<BlockscoutAddressInfo>(`/addresses/${address}`);
}

/**
 * Get deployment information for a contract
 */
export async function getDeploymentInfo(contractAddress: string): Promise<DeploymentInfo | null> {
  try {
    const data = await getAddressInfo(contractAddress);
    
    if (!data.is_contract) {
      console.warn(`[Blockscout] ${contractAddress} is not a contract`);
      return null;
    }

    return {
      contractAddress: data.hash,
      creatorAddress: data.creator_address_hash || '',
      creationTxHash: data.creation_tx_hash || '',
      blockNumber: data.block_number_balance_updated_at || 0,
      timestamp: new Date().toISOString(), // Will need to fetch from tx for exact timestamp
      isContract: data.is_contract || false,
      isVerified: data.is_verified || false,
      name: data.name,
    };
  } catch (error) {
    console.error('[Blockscout] Error fetching deployment info:', error);
    return null;
  }
}

/**
 * Get all token balances for an address
 */
export async function getAddressTokenBalances(
  address: string
): Promise<BlockscoutTokenBalance[]> {
  try {
    const data = await blockscoutFetch<PaginatedResponse<BlockscoutTokenBalance>>(
      `/addresses/${address}/tokens`
    );
    return data.items || [];
  } catch (error) {
    console.error('[Blockscout] Error fetching token balances:', error);
    return [];
  }
}

// ==================== TOKEN INFO API ====================

export interface TokenInfo {
  address: string;
  name: string;
  symbol: string;
  decimals: number;
  totalSupply: string;
  holdersCount: number;
  transfersCount: number;
  type: string;
  iconUrl?: string;
}

/**
 * Get token info including holders count and transfers count
 * Fetches from /tokens/{tokenAddress}
 */
export async function getTokenInfo(tokenAddress: string): Promise<TokenInfo | null> {
  try {
    const data = await blockscoutFetch<any>(`/tokens/${tokenAddress}`);
    
    return {
      address: data.address || tokenAddress,
      name: data.name || '',
      symbol: data.symbol || '',
      decimals: parseInt(data.decimals) || 18,
      totalSupply: data.total_supply || '0',
      holdersCount: data.holders || data.holders_count || 0,
      transfersCount: data.transfers_count || 0,
      type: data.type || 'ERC-20',
      iconUrl: data.icon_url,
    };
  } catch (error) {
    console.error('[Blockscout] Error fetching token info:', error);
    return null;
  }
}

/**
 * Get pool liquidity by fetching WETH balance and doubling it
 * Fetches from /addresses/{poolAddress}/tokens
 */
export async function getPoolLiquidityUSD(
  poolAddress: string,
  ethPriceUSD: number
): Promise<{ liquidityUSD: number; wethBalance: number } | null> {
  try {
    const tokens = await getAddressTokenBalances(poolAddress);
    
    // Find WETH balance
    const wethToken = tokens.find(
      t => (t.token?.address || t.token?.address_hash)?.toLowerCase() === WETH_ADDRESS.toLowerCase()
    );
    
    if (!wethToken) {
      console.log('[Blockscout] No WETH found in pool');
      return null;
    }
    
    // Parse WETH balance (18 decimals)
    const wethBalance = parseFloat(wethToken.value) / 1e18;
    
    // Liquidity = WETH value x 2 (since pool is 50/50)
    const liquidityUSD = wethBalance * ethPriceUSD * 2;
    
    return {
      liquidityUSD,
      wethBalance,
    };
  } catch (error) {
    console.error('[Blockscout] Error fetching pool liquidity:', error);
    return null;
  }
}

// ==================== TOKEN TRANSFERS API ====================

/**
 * Get token transfers for an address with pagination
 * @param maxPages - Maximum number of pages to fetch (default 3 for ~150 transfers)
 */
export async function getTokenTransfers(
  address: string,
  params?: {
    type?: 'ERC-20' | 'ERC-721' | 'ERC-1155';
    filter?: 'to' | 'from';
    token?: string;
    maxPages?: number;
  }
): Promise<BlockscoutTokenTransfer[]> {
  try {
    const allTransfers: BlockscoutTokenTransfer[] = [];
    const maxPages = params?.maxPages || 3; // Fetch up to 3 pages by default
    let currentPage = 0;
    let nextPageParams: any = null;
    
    while (currentPage < maxPages) {
      const queryParams = new URLSearchParams();
      if (params?.type) queryParams.set('type', params.type);
      if (params?.filter) queryParams.set('filter', params.filter);
      if (params?.token) queryParams.set('token', params.token);
      
      // Add pagination params if we have them
      if (nextPageParams) {
        if (nextPageParams.block_number) queryParams.set('block_number', nextPageParams.block_number.toString());
        if (nextPageParams.index) queryParams.set('index', nextPageParams.index.toString());
        if (nextPageParams.items_count) queryParams.set('items_count', nextPageParams.items_count.toString());
      }
      
      const query = queryParams.toString();
      const endpoint = `/addresses/${address}/token-transfers${query ? `?${query}` : ''}`;
      
      const data = await blockscoutFetch<PaginatedResponse<BlockscoutTokenTransfer>>(endpoint);
      
      if (data.items && data.items.length > 0) {
        allTransfers.push(...data.items);
      }
      
      // Check if there are more pages
      if (data.next_page_params) {
        nextPageParams = data.next_page_params;
        currentPage++;
      } else {
        break; // No more pages
      }
    }
    
    console.log(`[Blockscout] Fetched ${allTransfers.length} transfers across ${currentPage + 1} pages`);
    return allTransfers;
  } catch (error) {
    console.error('[Blockscout] Error fetching token transfers:', error);
    return [];
  }
}

/**
 * Get all token transfers for a specific token contract
 */
export async function getTokenContractTransfers(
  tokenAddress: string
): Promise<BlockscoutTokenTransfer[]> {
  try {
    const data = await blockscoutFetch<PaginatedResponse<BlockscoutTokenTransfer>>(
      `/tokens/${tokenAddress}/transfers`
    );
    return data.items || [];
  } catch (error) {
    console.error('[Blockscout] Error fetching token contract transfers:', error);
    return [];
  }
}

// ==================== TRANSACTIONS API ====================

/**
 * Get transactions for an address
 */
export async function getAddressTransactions(
  address: string,
  params?: {
    filter?: 'to' | 'from';
  }
): Promise<BlockscoutTransaction[]> {
  try {
    const queryParams = new URLSearchParams();
    if (params?.filter) queryParams.set('filter', params.filter);
    
    const query = queryParams.toString();
    const endpoint = `/addresses/${address}/transactions${query ? `?${query}` : ''}`;
    
    const data = await blockscoutFetch<PaginatedResponse<BlockscoutTransaction>>(endpoint);
    return data.items || [];
  } catch (error) {
    console.error('[Blockscout] Error fetching transactions:', error);
    return [];
  }
}

/**
 * Get internal transactions for an address
 */
export async function getInternalTransactions(
  address: string
): Promise<any[]> {
  try {
    const data = await blockscoutFetch<PaginatedResponse<any>>(
      `/addresses/${address}/internal-transactions`
    );
    return data.items || [];
  } catch (error) {
    console.error('[Blockscout] Error fetching internal transactions:', error);
    return [];
  }
}

/**
 * Get transaction details by hash
 */
export async function getTransaction(txHash: string): Promise<BlockscoutTransaction | null> {
  try {
    return await blockscoutFetch<BlockscoutTransaction>(`/transactions/${txHash}`);
  } catch (error) {
    console.error('[Blockscout] Error fetching transaction:', error);
    return null;
  }
}

// ==================== LOGS API ====================

/**
 * Get logs for an address (useful for event tracking)
 */
export async function getAddressLogs(address: string): Promise<BlockscoutLog[]> {
  try {
    const data = await blockscoutFetch<PaginatedResponse<BlockscoutLog>>(
      `/addresses/${address}/logs`
    );
    return data.items || [];
  } catch (error) {
    console.error('[Blockscout] Error fetching logs:', error);
    return [];
  }
}

// ==================== POOL & TRADE DATA ====================

/**
 * Get pool liquidity information
 */
export async function getPoolLiquidity(poolAddress: string): Promise<PoolLiquidity | null> {
  try {
    const tokens = await getAddressTokenBalances(poolAddress);
    
    if (tokens.length < 2) {
      console.warn('[Blockscout] Pool has less than 2 tokens');
      return null;
    }

    // Sort to ensure consistent ordering (WETH typically token1)
    const sortedTokens = tokens
      .filter(t => t.token.type === 'ERC-20')
      .sort((a, b) => {
        // Put WETH second
        if (a.token.address.toLowerCase() === WETH_ADDRESS.toLowerCase()) return 1;
        if (b.token.address.toLowerCase() === WETH_ADDRESS.toLowerCase()) return -1;
        return 0;
      });

    if (sortedTokens.length < 2) {
      return null;
    }

    const token0 = sortedTokens[0];
    const token1 = sortedTokens[1];

    return {
      poolAddress,
      token0: {
        address: token0.token.address,
        symbol: token0.token.symbol,
        name: token0.token.name,
        decimals: parseInt(token0.token.decimals),
        balance: token0.value,
        balanceFormatted: formatTokenAmount(token0.value, token0.token.decimals),
      },
      token1: {
        address: token1.token.address,
        symbol: token1.token.symbol,
        name: token1.token.name,
        decimals: parseInt(token1.token.decimals),
        balance: token1.value,
        balanceFormatted: formatTokenAmount(token1.value, token1.token.decimals),
      },
    };
  } catch (error) {
    console.error('[Blockscout] Error fetching pool liquidity:', error);
    return null;
  }
}

// Known contract addresses to filter out (fee collections, not trades)
const FEE_COLLECTOR_ADDRESSES = [
  '0x0f417fab5b78aacc265137ebd83b41c44f515d3f', // MavrkLPLocker V1 (NFT positions)
  '0x89264dbc5cdc22bbb00ecc672c717d7205647702', // MavrkLPLocker V2 (verified on explorer)
  '0xd2c5947a6777500d9b7ad55a4d48aef855ae6aba', // MavrkTokenLocker
  '0x9496ff7a7be0a91f582baa96ac12a0a36300750c', // MavrkLinearVesting
  '0x6215eac0889389b359677a2b771c46d538001e75', // MavrkTokenFactory V2
  '0xe01118dbcf8ba2609d3ffbd6a53d9533c7526d60', // MavrkTokenFactory V3 (self-custody LP)
].map(a => a.toLowerCase());

// Known router addresses on Ink chain (these indicate real swaps)
const ROUTER_ADDRESSES = [
  '0x177778f19e89dd1012bdbe603f144088a95c4b53', // SwapRouter02 (Uniswap/Protofire)
  '0xb92fe925dc43a0ecde6c8b1a2709c170ec4fff4f', // RelayRouterV3
].map(a => a.toLowerCase());

/**
 * Get swap/trade history for a pool address
 * Parses token transfers to determine buy/sell activity
 * Filters out fee collections and liquidity operations
 */
export async function getPoolTrades(
  poolAddress: string,
  tokenAddress: string,
  ethPriceUSD: number = 3500 // Default ETH price, should be passed in
): Promise<OrderbookTrade[]> {
  try {
    console.log('[Blockscout] Fetching pool trades for:', poolAddress);
    
    // Get all ERC-20 token transfers to/from the pool
    const transfers = await getTokenTransfers(poolAddress, { type: 'ERC-20' });
    
    console.log(`[Blockscout] Found ${transfers.length} token transfers`);

    // Group transfers by transaction hash to pair token <-> WETH transfers
    const txGroups = new Map<string, BlockscoutTokenTransfer[]>();
    
    for (const transfer of transfers) {
      const txHash = getTxHash(transfer);
      if (!txHash) continue;
      const existing = txGroups.get(txHash) || [];
      existing.push(transfer);
      txGroups.set(txHash, existing);
    }
    

    const trades: OrderbookTrade[] = [];
    const normalizedTokenAddress = tokenAddress.toLowerCase();
    const normalizedPoolAddress = poolAddress.toLowerCase();

    for (const [txHash, txTransfers] of txGroups) {
      try {
        // Find the token transfer and WETH transfer in this transaction
        // Use helper to handle both address and address_hash field names
        const tokenTransfer = txTransfers.find(
          t => getTokenAddress(t).toLowerCase() === normalizedTokenAddress
        );
        const wethTransfer = txTransfers.find(
          t => getTokenAddress(t).toLowerCase() === WETH_ADDRESS.toLowerCase()
        );

        if (!tokenTransfer || !wethTransfer) {
          continue; // Not a swap transaction (needs both token and WETH movement)
        }

        // Get the addresses involved (excluding pool) - add null checks
        const tokenFrom = tokenTransfer.from?.hash?.toLowerCase() || '';
        const tokenTo = tokenTransfer.to?.hash?.toLowerCase() || '';
        const wethFrom = wethTransfer.from?.hash?.toLowerCase() || '';
        const wethTo = wethTransfer.to?.hash?.toLowerCase() || '';
        
        // Skip if any address is missing
        if (!tokenFrom || !tokenTo || !wethFrom || !wethTo) {
          continue;
        }

        // Filter out fee collections: if recipient is a known locker/vesting contract
        if (FEE_COLLECTOR_ADDRESSES.includes(tokenTo) || FEE_COLLECTOR_ADDRESSES.includes(wethTo)) {
          continue;
        }

        // For a valid swap, token and WETH should flow in opposite directions relative to pool
        // BUY: Token leaves pool, WETH enters pool
        // SELL: Token enters pool, WETH leaves pool
        const tokenLeavesPool = tokenFrom === normalizedPoolAddress;
        const tokenEntersPool = tokenTo === normalizedPoolAddress;
        const wethLeavesPool = wethFrom === normalizedPoolAddress;
        const wethEntersPool = wethTo === normalizedPoolAddress;

        // Valid swap patterns:
        // BUY: token OUT of pool, WETH INTO pool
        // SELL: token INTO pool, WETH OUT of pool
        const isBuy = tokenLeavesPool && wethEntersPool;
        const isSell = tokenEntersPool && wethLeavesPool;

        if (!isBuy && !isSell) {
          continue; // Not a standard swap pattern (could be liquidity add/remove, arbitrage, etc.)
        }

        // Safely parse amounts with null checks
        if (!tokenTransfer.total?.value || !tokenTransfer.total?.decimals ||
            !wethTransfer.total?.value || !wethTransfer.total?.decimals) {
          continue;
        }
        
        const tokenAmount = formatTokenAmount(tokenTransfer.total.value, tokenTransfer.total.decimals);
        const ethAmount = formatTokenAmount(wethTransfer.total.value, wethTransfer.total.decimals);
        
        // Skip dust amounts (likely failed txs or very small trades)
        if (tokenAmount < 0.0001 || ethAmount < 0.000001) {
          continue;
        }

        const pricePerToken = ethAmount / tokenAmount;
        const amountUSD = ethAmount * ethPriceUSD;

        // Determine the trader (the non-pool, non-router address)
        let maker = '';
        if (isBuy) {
          // For buys, the trader receives tokens - check who got the tokens
          maker = tokenTo !== normalizedPoolAddress ? tokenTo : wethFrom;
        } else {
          // For sells, the trader sends tokens - check who sent the tokens
          maker = tokenFrom !== normalizedPoolAddress ? tokenFrom : wethTo;
        }

        // If maker is a router, try to find the actual user
        if (ROUTER_ADDRESSES.includes(maker.toLowerCase())) {
          // The actual user is likely on the other side of the WETH transfer
          maker = isBuy ? wethFrom : wethTo;
        }

        trades.push({
          type: isBuy ? 'buy' : 'sell',
          tokenAmount,
          ethAmount,
          pricePerToken,
          priceUSD: pricePerToken * ethPriceUSD,
          amountUSD,
          timestamp: new Date(tokenTransfer.timestamp).getTime(),
          txHash,
          maker,
          blockNumber: tokenTransfer.block_number,
        });
      } catch (txError) {
        continue; // Skip problematic transactions
      }
    }

    // Sort by timestamp descending (most recent first)
    trades.sort((a, b) => b.timestamp - a.timestamp);

    console.log(`[Blockscout] Parsed ${trades.length} valid trades from ${txGroups.size} transactions`);
    return trades;
  } catch (error) {
    console.error('[Blockscout] Error fetching pool trades:', error);
    return [];
  }
}

/**
 * Calculate buy/sell aggregation for orderbook visualization
 */
export function aggregateOrderbook(
  trades: OrderbookTrade[],
  priceStep: number = 0.0001
): { buys: Map<number, number>; sells: Map<number, number> } {
  const buys = new Map<number, number>();
  const sells = new Map<number, number>();

  for (const trade of trades) {
    // Round price to step
    const priceLevel = Math.floor(trade.pricePerToken / priceStep) * priceStep;
    
    if (trade.type === 'buy') {
      const existing = buys.get(priceLevel) || 0;
      buys.set(priceLevel, existing + trade.tokenAmount);
    } else {
      const existing = sells.get(priceLevel) || 0;
      sells.set(priceLevel, existing + trade.tokenAmount);
    }
  }

  return { buys, sells };
}

// ==================== LOCK & VESTING API ====================

/**
 * Get all tokens locked in the MavrkTokenLocker contract
 */
export async function getLockedTokens(): Promise<BlockscoutTokenBalance[]> {
  return getAddressTokenBalances(config.ink.tokenLockerAddress);
}

/**
 * Get all tokens in the vesting contract
 */
export async function getVestedTokens(): Promise<BlockscoutTokenBalance[]> {
  return getAddressTokenBalances(config.ink.vestingContractAddress);
}

/**
 * Get lock events from the locker contract logs
 */
export async function getLockEvents(): Promise<BlockscoutLog[]> {
  return getAddressLogs(config.ink.tokenLockerAddress);
}

/**
 * Get vesting events from the vesting contract logs
 */
export async function getVestingEvents(): Promise<BlockscoutLog[]> {
  return getAddressLogs(config.ink.vestingContractAddress);
}

// ==================== LEGACY API V1 SUPPORT ====================

/**
 * Fetch token list for an address using API v1 (BlockScout standard)
 * This is maintained for backward compatibility
 */
export async function getTokenListV1(address: string): Promise<any[]> {
  try {
    const response = await fetch(
      `${BLOCKSCOUT_API_V1}?module=account&action=tokenlist&address=${address}`,
      { headers: getBlockscoutHeaders() }
    );
    
    const data = await response.json();
    
    if (data.status === '1' && Array.isArray(data.result)) {
      return data.result;
    }
    
    return [];
  } catch (error) {
    console.error('[Blockscout V1] Error fetching token list:', error);
    return [];
  }
}

// ==================== BATCH OPERATIONS ====================

/**
 * Fetch multiple addresses' token balances in parallel
 */
export async function batchGetTokenBalances(
  addresses: string[]
): Promise<Map<string, BlockscoutTokenBalance[]>> {
  const results = new Map<string, BlockscoutTokenBalance[]>();
  
  // Batch in groups of 5 to avoid rate limiting
  const batchSize = 5;
  
  for (let i = 0; i < addresses.length; i += batchSize) {
    const batch = addresses.slice(i, i + batchSize);
    
    const promises = batch.map(async (address) => {
      const balances = await getAddressTokenBalances(address);
      return { address, balances };
    });
    
    const batchResults = await Promise.all(promises);
    
    for (const { address, balances } of batchResults) {
      results.set(address, balances);
    }
    
    // Small delay between batches
    if (i + batchSize < addresses.length) {
      await new Promise(resolve => setTimeout(resolve, 200));
    }
  }
  
  return results;
}

/**
 * Export utility for formatting addresses consistently
 */
export function normalizeAddress(address: string): string {
  return address.toLowerCase();
}

export default {
  // Address operations
  getAddressInfo,
  getDeploymentInfo,
  getAddressTokenBalances,
  
  // Token info
  getTokenInfo,
  getPoolLiquidityUSD,
  
  // Token transfers
  getTokenTransfers,
  getTokenContractTransfers,
  
  // Transactions
  getAddressTransactions,
  getInternalTransactions,
  getTransaction,
  
  // Logs
  getAddressLogs,
  
  // Pool & Trade data
  getPoolLiquidity,
  getPoolTrades,
  aggregateOrderbook,
  
  // Lock & Vesting
  getLockedTokens,
  getVestedTokens,
  getLockEvents,
  getVestingEvents,
  
  // Legacy
  getTokenListV1,
  
  // Batch
  batchGetTokenBalances,
  
  // Helpers
  getBlockscoutHeaders,
  normalizeAddress,
};

